import type { SpeakOptions } from "./types"
import { ElMessageBox } from "element-plus"
import autoplayPermissionGuideSrc from "@/assets/tts/autoplay-permission-guide.png"
import { appendApiGrayHeader } from "@/utils/apiGrayHeader"
import { getToken } from "@/utils/cache/cookies"

const MERCHANT_LOCAL_TTS_STREAM_PATH = "/teashop_merchant/tts/audio/speech/stream"
const DEFAULT_LOCAL_TTS_VOICE = "shimmer"
const LOCAL_TTS_AUDIO_MIME_TYPE = "audio/mpeg"
const LOCAL_TTS_REQUEST_TIMEOUT_MS = 30000
const LOCAL_TTS_RESPONSE_TIMEOUT_MS = 90000
const LOCAL_TTS_STREAM_IDLE_TIMEOUT_MS = 60000
let autoplayPromptOpen = false

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, "")
}

function getLocalTTSBaseURL() {
  return trimTrailingSlash(import.meta.env.VITE_BASE_API?.trim() || "")
}

function joinUrl(baseURL: string, path: string) {
  if (!baseURL) {
    return path
  }
  return `${baseURL.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`
}

function buildSpeechURL() {
  return joinUrl(getLocalTTSBaseURL(), MERCHANT_LOCAL_TTS_STREAM_PATH)
}

function appendZodanceVersionHeader(headers: Headers) {
  const zodanceVersion = import.meta.env.VITE_ZODANCE_VERSION?.trim()
  if (zodanceVersion) {
    headers.set("ZODANCE-VERSION", zodanceVersion)
  }
}

function normalizeTextOption(value: string | undefined) {
  return value?.trim() || undefined
}

function normalizePositiveNumber(value: number | undefined, fallback: number) {
  if (!Number.isFinite(value) || (value as number) <= 0) return fallback
  return value as number
}

function isMediaSourceStreamingSupported() {
  return (
    typeof MediaSource !== "undefined" &&
    typeof MediaSource.isTypeSupported === "function" &&
    MediaSource.isTypeSupported(LOCAL_TTS_AUDIO_MIME_TYPE)
  )
}

function isLocalTTSStreamingEnabled() {
  return import.meta.env.VITE_LOCAL_TTS_STREAMING === "true"
}

async function readErrorMessage(response: Response) {
  try {
    const data = await response.json()
    return data?.error?.message ?? data?.msg ?? data?.message ?? data?.data ?? response.statusText
  } catch (_error) {
    return response.statusText
  }
}

function isJsonResponse(response: Response) {
  const contentType = response.headers.get("content-type")?.toLowerCase() ?? ""
  return contentType.includes("application/json") || contentType.includes("+json")
}

function isAutoplayBlockedError(error: unknown) {
  if (!(error instanceof Error)) return false
  const message = error.message.toLowerCase()
  return error.name === "NotAllowedError" || message.includes("autoplay") || message.includes("user didn't interact")
}

function createTimeoutError(message: string) {
  const error = new Error(message)
  error.name = "TimeoutError"
  return error
}

function readStreamChunk(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  timeoutMs: number,
  onTimeout: () => void,
) {
  let timeoutId: number | undefined
  return new Promise<ReadableStreamReadResult<Uint8Array>>((resolve, reject) => {
    timeoutId = window.setTimeout(() => {
      onTimeout()
      reject(createTimeoutError(`LocalTTS streaming response stalled for ${timeoutMs}ms`))
    }, timeoutMs)

    reader.read().then(resolve, reject).finally(() => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }
    })
  })
}

function endMediaSource(mediaSource: MediaSource) {
  if (mediaSource.readyState !== "open") return
  try {
    mediaSource.endOfStream()
  } catch (_error) {
    // ignore
  }
}

async function promptAutoplayPermission(audio: HTMLAudioElement) {
  if (autoplayPromptOpen) {
    throw new Error("Audio autoplay prompt is already open")
  }

  autoplayPromptOpen = true
  try {
    await ElMessageBox.confirm(
      [
        "<div style='line-height: 1.7; text-align: left;'>",
        "<p>浏览器已阻止自动语音播报。</p>",
        "<p>请点击「立即播放」继续本次语音；如需后续自动播报，请在地址栏左侧权限图标中，将本网站的自动播放音频权限设置为允许。</p>",
        "<p>Firefox 可在地址栏左侧权限/锁形图标中调整 Autoplay 设置。</p>",
        `<img src="${autoplayPermissionGuideSrc}" alt="Firefox 自动播放权限设置示意图" style="display: block; width: 100%; max-width: 520px; margin: 12px auto 0; border: 1px solid #e4e7ed; border-radius: 6px;" />`,
        "</div>",
      ].join(""),
      "开启语音播报",
      {
        dangerouslyUseHTMLString: true,
        confirmButtonText: "立即播放",
        cancelButtonText: "稍后再说",
        closeOnClickModal: false,
        closeOnPressEscape: false,
        type: "warning",
      },
    )
    await audio.play()
  } finally {
    autoplayPromptOpen = false
  }
}

async function playAudio(audio: HTMLAudioElement) {
  try {
    await audio.play()
  } catch (error) {
    if (isAutoplayBlockedError(error)) {
      await promptAutoplayPermission(audio)
      return
    }
    throw error
  }
}

function appendBuffer(sourceBuffer: SourceBuffer, chunk: Uint8Array) {
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      sourceBuffer.removeEventListener("updateend", handleUpdateEnd)
      sourceBuffer.removeEventListener("error", handleError)
    }
    const handleUpdateEnd = () => {
      cleanup()
      resolve()
    }
    const handleError = () => {
      cleanup()
      reject(new Error("LocalTTS MediaSource buffer append failed"))
    }

    sourceBuffer.addEventListener("updateend", handleUpdateEnd, { once: true })
    sourceBuffer.addEventListener("error", handleError, { once: true })
    const buffer = new ArrayBuffer(chunk.byteLength)
    new Uint8Array(buffer).set(chunk)
    sourceBuffer.appendBuffer(buffer)
  })
}

async function createAudioFromBlob(
  response: Response,
  onFinish: SpeakOptions["onFinish"],
  stopFetch: () => void,
  clearNetworkTimeout: () => void,
) {
  const audioBlob = await response.blob()
  clearNetworkTimeout()
  const audioURL = URL.createObjectURL(audioBlob)
  const audio = new Audio(audioURL)
  const cleanup = () => URL.revokeObjectURL(audioURL)

  audio.addEventListener("ended", () => {
    cleanup()
    onFinish?.()
  }, { once: true })
  audio.addEventListener("error", cleanup, { once: true })
  try {
    await playAudio(audio)
  } catch (error) {
    stopFetch()
    cleanup()
    throw error
  }

  return {
    stopSpeak: () => {
      stopFetch()
      audio.pause()
      audio.currentTime = 0
      cleanup()
    },
  }
}

async function createStreamingAudio(
  response: Response,
  onFinish: SpeakOptions["onFinish"],
  stopFetch: () => void,
  clearNetworkTimeout: () => void,
) {
  if (!response.body || !isMediaSourceStreamingSupported()) {
    return createAudioFromBlob(response, onFinish, stopFetch, clearNetworkTimeout)
  }

  const mediaSource = new MediaSource()
  const audioURL = URL.createObjectURL(mediaSource)
  const audio = new Audio(audioURL)
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null
  let stopped = false
  let cleaned = false

  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    const activeReader = reader
    reader = null
    if (activeReader) {
      activeReader.cancel().catch(() => {
        // ignore
      })
    }
    endMediaSource(mediaSource)
    URL.revokeObjectURL(audioURL)
  }

  const sourceBuffer = await new Promise<SourceBuffer>((resolve, reject) => {
    mediaSource.addEventListener("sourceopen", () => {
      try {
        const buffer = mediaSource.addSourceBuffer(LOCAL_TTS_AUDIO_MIME_TYPE)
        try {
          buffer.mode = "sequence"
        } catch (_error) {
          // Some browsers keep MP3 source buffers in their default mode.
        }
        resolve(buffer)
      } catch (error) {
        reject(error)
      }
    }, { once: true })
  })

  reader = response.body.getReader()
  const firstChunk = await readStreamChunk(reader, LOCAL_TTS_REQUEST_TIMEOUT_MS, stopFetch)
  if (firstChunk.done || !firstChunk.value?.byteLength) {
    cleanup()
    throw new Error("LocalTTS streaming response is empty")
  }
  await appendBuffer(sourceBuffer, firstChunk.value)
  clearNetworkTimeout()

  const streamReader = reader
  const pumpPromise = (async () => {
    for (;;) {
      if (stopped) break
      const { done, value } = await readStreamChunk(streamReader, LOCAL_TTS_STREAM_IDLE_TIMEOUT_MS, stopFetch)
      if (done || stopped) break
      if (value?.byteLength) {
        await appendBuffer(sourceBuffer, value)
      }
    }
    if (!stopped && mediaSource.readyState === "open") {
      endMediaSource(mediaSource)
    }
  })()

  pumpPromise.catch((error) => {
    if (!stopped) {
      console.warn("[tts] LocalTTS streaming playback failed", error)
      stopFetch()
      endMediaSource(mediaSource)
    }
  })

  audio.addEventListener("ended", () => {
    cleanup()
    onFinish?.()
  }, { once: true })
  audio.addEventListener("error", () => {
    stopped = true
    stopFetch()
    cleanup()
  }, { once: true })
  try {
    await playAudio(audio)
  } catch (error) {
    stopped = true
    stopFetch()
    cleanup()
    throw error
  }

  return {
    stopSpeak: () => {
      stopped = true
      stopFetch()
      audio.pause()
      audio.currentTime = 0
      cleanup()
    },
  }
}

export async function speak({ text, voice, model, speed, onFinish }: SpeakOptions) {
  const controller = new AbortController()
  let timeoutId: number | undefined = window.setTimeout(() => controller.abort(), LOCAL_TTS_REQUEST_TIMEOUT_MS)
  const clearNetworkTimeout = () => {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId)
      timeoutId = undefined
    }
  }
  const resetNetworkTimeout = (timeoutMs: number) => {
    clearNetworkTimeout()
    timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)
  }
  const headers = new Headers({
    "Content-Type": "application/json; charset=utf-8",
    Accept: "audio/mpeg, application/json",
    "Tenant-Id": "teashop",
  })
  const token = getToken()
  if (token) {
    headers.set("Authorization", `Bearer ${token}`)
  }
  appendZodanceVersionHeader(headers)
  appendApiGrayHeader(headers)

  const requestBody: Record<string, unknown> = {
    input: text,
    voice: normalizeTextOption(voice) ?? normalizeTextOption(import.meta.env.VITE_LOCAL_TTS_VOICE) ?? DEFAULT_LOCAL_TTS_VOICE,
    speed: normalizePositiveNumber(speed, 1),
  }
  const resolvedModel = normalizeTextOption(model) ?? normalizeTextOption(import.meta.env.VITE_LOCAL_TTS_MODEL)
  if (resolvedModel) {
    requestBody.model = resolvedModel
  }

  try {
    const response = await fetch(buildSpeechURL(), {
      method: "POST",
      headers,
      signal: controller.signal,
      body: JSON.stringify(requestBody),
    })

    if (!response.ok || isJsonResponse(response)) {
      throw new Error(`LocalTTS request failed: ${response.status} ${await readErrorMessage(response)}`)
    }

    if (isLocalTTSStreamingEnabled()) {
      return await createStreamingAudio(response, onFinish, () => controller.abort(), clearNetworkTimeout)
    }
    resetNetworkTimeout(LOCAL_TTS_RESPONSE_TIMEOUT_MS)
    return await createAudioFromBlob(response, onFinish, () => controller.abort(), clearNetworkTimeout)
  } finally {
    clearNetworkTimeout()
  }
}
